import React from "react";
import { useNavigate } from "react-router-dom";
import { IVenda } from "shared/interfaces/IVenda";
import { formatarNumeroPedido } from "shared/utils/compraStatus";
import { formatarMoeda, reaisParaCentavos } from "../../utils/money";
import styles from "./VendaItem.module.scss";

interface VendaItemProps {
  venda: IVenda;
}

export const VendaItem: React.FC<VendaItemProps> = ({ venda }) => {
  const navigate = useNavigate();
  const itens = venda.items ?? [];

  const formatarData = (data: any) => {
    if (!data) return "";

    const date = data.seconds
      ? new Date(data.seconds * 1000)
      : new Date(data);

    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const quantidadeItens = itens.length;

  const imagens = itens
    .map((item) => item.thumbnailUrl)
    .filter(Boolean)
    .slice(0, 4);

  const primeiroItem = itens[0];

  const abrirDetalhes = () => {
    navigate(`/minhasvendas/${venda.id}/detalhes`);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.containerInfo}>
          <div className={styles.info}>
            <strong>Venda</strong>
            <p className={styles.codigo}>
              #{formatarNumeroPedido(venda.id)}
            </p>
          </div>

          <span className={styles.data}>
            {formatarData(venda.data)} · {venda.clienteNome}
          </span>
        </div>

        <span className={`${styles.badge} ${styles[venda.status]}`}>
          {venda.status}
        </span>
      </div>

      <div className={styles.divisor} />

      <div
        className={styles.produtoResumo}
        onClick={abrirDetalhes}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            abrirDetalhes();
          }
        }}
      >
        <div className={styles.mosaico}>
          {imagens.length > 0 ? (
            imagens.map((imagem, index) => (
              <div
                key={`${imagem}-${index}`}
                className={`${styles.fatia} ${
                  styles[`fatia${Math.min(imagens.length, 4)}`]
                }`}
              >
                <img
                  src={imagem}
                  alt={itens[index]?.productName || "Produto da venda"}
                />
              </div>
            ))
          ) : (
            <div className={styles.semImagem}>
              <img src="/imagens/foto.png" alt="Produto" />
            </div>
          )}
        </div>

        <div className={styles.resumoItens}>
          <strong>
            {quantidadeItens}{" "}
            {quantidadeItens === 1 ? "item" : "itens"}
          </strong>

          {quantidadeItens === 1 && primeiroItem?.variationText && (
            <span className={styles.variacoes}>
              {primeiroItem.variationText}
            </span>
          )}

          {quantidadeItens === 1 &&
            (primeiroItem?.sellerSku || primeiroItem?.internalSku) && (
              <span className={styles.sku}>
                SKU {primeiroItem.sellerSku || primeiroItem.internalSku}
                {primeiroItem.sellerSku && primeiroItem.internalSku
                  ? ` · ${primeiroItem.internalSku}`
                  : ""}
              </span>
            )}

          <span className={styles.verDetalhes}>
            Clique para ver os detalhes
          </span>
        </div>
      </div>

      <div className={styles.divisor} />

      <div className={styles.containerTotal}>
        <span>Recebido</span>
        <p className={styles.total}>
          {formatarMoeda(reaisParaCentavos(venda.total ?? 0))}
        </p>
      </div>
    </div>
  );
};
